<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import {
  AddIcon,
  PlayIcon,
  LinkIcon,
  UploadIcon,
  WebhookIcon,
  ClockIcon,
  ReplayIcon,
} from "../icons";
import { formatValue, type Field } from "@yawhub/core";
import { api, errorText, dateTime, useSession } from "../api";
import FieldEditor from "./FieldEditor.vue";
const props = defineProps<{ dataset: string; fields: Field[] }>(),
  emit = defineEmits(["changed", "schemaChanged"]),
  session = useSession();
const drift = ref<any>(null),
  driftPreview = ref<any>(null);
watch(
  () => drift.value?.schema,
  () => {
    driftPreview.value = null;
  },
  { deep: true },
);
const sources = ref<any[]>([]),
  schedules = ref<any[]>([]),
  raws = ref<any[]>([]),
  error = ref(""),
  message = ref(""),
  busy = ref(false),
  adding = ref(false),
  secret = ref<any>(null),
  scheduleSource = ref(""),
  cron = ref("0 * * * *"),
  timezone = ref("Europe/Copenhagen"),
  uploadPreview = ref<any>(null),
  mappingEdit = ref<any>(null),
  mappingText = ref(""),
  mappingPreview = ref<any>(null),
  reprocess = ref<any>(null);
const name = ref(""),
  kind = ref<"http" | "file" | "webhook" | "mcp">("http"),
  url = ref(""),
  itemsPath = ref(""),
  credential = ref(""),
  format = ref("csv"),
  identity = ref(props.fields[0]?.key ?? ""),
  paths = ref(Object.fromEntries(props.fields.map((f) => [f.id, f.key])));
const mcpTools = ref<any[]>([]),
  mcpTool = ref(""),
  mcpArguments = ref("{}"),
  mcpCursorArgument = ref(""),
  mcpNextCursorPath = ref("");
watch([url, credential], () => {
  mcpTools.value = [];
  mcpTool.value = "";
});
async function discoverMcp() {
  await action(async () => {
    const result = await api("/api/mcp/sources/discover", {
      method: "POST",
      body: {
        url: url.value,
        ...(credential.value
          ? { credentials: { type: "bearer", value: credential.value } }
          : {}),
      },
    });
    mcpTools.value = result.tools;
    mcpTool.value = result.tools[0]?.name ?? "";
    message.value = result.tools.length
      ? "Vælg det læseværktøj, der skal levere data."
      : "Serveren angiver ingen læseværktøjer. Værktøjet skal være markeret readOnlyHint.";
  });
}
const base = "/api/datasets/" + props.dataset;
watch([mappingText, mappingEdit], () => {
  mappingPreview.value = null;
});
async function load() {
  try {
    [sources.value, schedules.value, raws.value] = await Promise.all([
      api(base + "/sources"),
      api(base + "/schedules"),
      api(base + "/raw"),
    ]);
  } catch (e) {
    error.value = errorText(e);
  }
}
onMounted(load);
async function action(fn: () => Promise<void>) {
  busy.value = true;
  error.value = "";
  message.value = "";
  try {
    await fn();
    await load();
    emit("changed");
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function add() {
  await action(async () => {
    let args: unknown;
    if (kind.value === "mcp") {
      try {
        args = JSON.parse(mcpArguments.value);
      } catch {
        throw new Error(
          "Argumenterne skal være gyldig JSON. Kontrollér anførselstegn og kommaer.",
        );
      }
      if (!args || Array.isArray(args) || typeof args !== "object")
        throw new Error("Argumenterne skal være et JSON-objekt, fx {}.");
    }
    const mapping = {
      fields: props.fields
        .filter((f) => f.active && !f.calculation)
        .map((f) => ({
          fieldId: f.id,
          path: paths.value[f.id] ?? f.key,
          transform: ["integer", "decimal", "rating"].includes(f.type)
            ? "number"
            : f.type === "enum"
              ? "string"
              : f.type,
        })),
      identity: {
        mode: "external",
        paths: identity.value.split(",").map((s) => s.trim()),
      },
      ignoredPaths: [],
    };
    const r = await api(base + "/sources", {
      method: "POST",
      body: {
        name: name.value,
        kind: kind.value,
        config:
          kind.value === "http"
            ? { url: url.value, itemsPath: itemsPath.value }
            : kind.value === "mcp"
              ? {
                  url: url.value,
                  tool: mcpTool.value,
                  toolHash: mcpTools.value.find((t) => t.name === mcpTool.value)
                    ?.toolHash,
                  arguments: args,
                  itemsPath: itemsPath.value,
                  ...(mcpCursorArgument.value
                    ? {
                        cursorArgument: mcpCursorArgument.value,
                        nextCursorPath: mcpNextCursorPath.value,
                      }
                    : {}),
                }
              : kind.value === "file"
                ? { format: format.value }
                : { itemsPath: itemsPath.value },
        mapping,
        ...(credential.value && ["http", "mcp"].includes(kind.value)
          ? { credentials: { type: "bearer", value: credential.value } }
          : {}),
      },
    });
    adding.value = false;
    credential.value = "";
    secret.value = r.secret ? r : null;
    message.value = "Datakilden er oprettet. Kør den, når du er klar.";
  });
}
async function run(s: any) {
  await action(async () => {
    await api(base + "/bindings/" + s.binding_id + "/run", { method: "POST" });
    message.value = "Importen er sat i kø. Følg resultatet under Kørsler.";
  });
}
async function upload(s: any, event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  await action(async () => {
    const data = new FormData();
    data.append("file", file);
    uploadPreview.value = {
      ...(await api(base + "/bindings/" + s.binding_id + "/upload", {
        method: "POST",
        body: data,
      })),
      binding: s.binding_id,
    };
  });
  (event.target as HTMLInputElement).value = "";
}
async function commitUpload() {
  await action(async () => {
    await api(
      base + "/bindings/" + uploadPreview.value.binding + "/import-file",
      { method: "POST", body: { rawId: uploadPreview.value.rawId } },
    );
    uploadPreview.value = null;
    message.value = "Filimporten er sat i kø.";
  });
}
async function schedule() {
  await action(async () => {
    await api(base + "/schedules", {
      method: "POST",
      body: {
        kind: "import",
        targetId: scheduleSource.value,
        cron: cron.value,
        timezone: timezone.value,
      },
    });
    scheduleSource.value = "";
    message.value = "Tidsplanen er gemt.";
  });
}
async function saveMapping() {
  await action(async () => {
    await api(base + "/bindings/" + mappingEdit.value.binding_id + "/mapping", {
      method: "PUT",
      body: { draftId: mappingPreview.value.draftId },
    });
    mappingEdit.value = null;
    message.value =
      "Ny mapping-version er aktiveret. Eksisterende rådata kan genbehandles nedenfor.";
  });
}
async function previewMapping() {
  await action(async () => {
    mappingPreview.value = await api(
      base + "/bindings/" + mappingEdit.value.binding_id + "/mapping/preview",
      { method: "POST", body: JSON.parse(mappingText.value) },
    );
  });
}
async function rawPreview(raw: any) {
  await action(async () => {
    reprocess.value = await api(
      base + "/raw/" + raw.id + "/reprocess-preview",
      { method: "POST" },
    );
  });
}
async function applyReprocess() {
  await action(async () => {
    await api(base + "/reprocess/" + reprocess.value.id, { method: "POST" });
    reprocess.value = null;
    message.value = "Genbehandling er sat i kø.";
  });
}
async function proposeFields(source: any, useAi = false) {
  await action(async () => {
    drift.value = {
      ...(await api(
        base + "/bindings/" + source.binding_id + "/drift-proposal",
        { method: "POST", body: { useAi } },
      )),
      source,
    };
    driftPreview.value = null;
  });
}
async function previewFields() {
  await action(async () => {
    driftPreview.value = await api(base + "/schema/preview", {
      method: "POST",
      body: drift.value.schema,
    });
  });
}
async function acceptFields() {
  await action(async () => {
    await api(base + "/schema/" + driftPreview.value.id + "/activate", {
      method: "POST",
    });
    const proposal = drift.value;
    mappingEdit.value = proposal.source;
    mappingText.value = JSON.stringify(
      {
        ...proposal.mapping,
        fields: proposal.mapping.fields.filter((m: any) =>
          proposal.schema.fields.some(
            (f: Field) => f.id === m.fieldId && f.active,
          ),
        ),
      },
      null,
      2,
    );
    drift.value = null;
    driftPreview.value = null;
    emit("schemaChanged");
    message.value =
      "Felterne er godkendt. Gennemgå nu mapping-previewet og aktivér mappingen, før du genimporterer eller genbehandler rådata.";
  });
}
</script>
<template>
  <div class="section-heading">
    <div>
      <h2>Datakilder</h2>
      <p class="muted">Hver kilde har sin egen identitet og mapping.</p>
    </div>
    <button class="button primary" @click="adding = !adding">
      <AddIcon :size="17" />Tilføj kilde
    </button>
  </div>
  <p v-if="error" class="error" role="alert">{{ error }}</p>
  <p v-if="message" class="notice success-notice" role="status">
    {{ message }}
  </p>
  <div v-if="secret" class="secret-box">
    <h3>Gem din webhook-nøgle</h3>
    <p>
      Nøglen vises kun her. Signér timestamp + “.” + de præcise body-bytes med
      HMAC-SHA256.
    </p>
    <label>Endpoint<input readonly :value="secret.webhookUrl" /></label
    ><label>Secret<input readonly :value="secret.secret" /></label>
    <p class="small">
      Headers: X-Yawhub-Event-Id, X-Yawhub-Timestamp (Unix-sekunder),
      X-Yawhub-Signature (hex).
    </p>
    <button class="button secondary" @click="secret = null">
      Jeg har gemt nøglen
    </button>
  </div>
  <form v-if="adding" class="inline-editor form-stack" @submit.prevent="add">
    <h3>Forbind en ny kilde</h3>
    <div class="form-grid">
      <label
        >Navn<input v-model="name" required placeholder="Fx Salgs-API" /></label
      ><label
        >Type<select v-model="kind">
          <option value="http">HTTP API</option>
          <option value="file">Genanvendelig filprofil</option>
          <option value="webhook">Signeret webhook</option>
          <option value="mcp">MCP-server</option>
        </select></label
      ><label v-if="['http', 'mcp'].includes(kind)"
        >HTTPS URL<input v-model="url" type="url" required /></label
      ><label v-if="kind !== 'file'"
        >Sti til JSON-listen<input
          v-model="itemsPath"
          placeholder="Fx data.items" /></label
      ><label v-if="['http', 'mcp'].includes(kind)"
        >Bearer-token, valgfrit<input
          v-model="credential"
          type="password"
          autocomplete="off" /></label
      ><label v-if="kind === 'file'"
        >Filformat<select v-model="format">
          <option value="csv">CSV</option>
          <option value="xlsx">Excel (XLSX)</option>
        </select></label
      ><label
        >Identitetssti(er)<input
          v-model="identity"
          required
          placeholder="Fx id"
      /></label>
    </div>
    <template v-if="kind === 'mcp'">
      <p class="muted small">
        En administrator vælger ét læseværktøj. Data importeres med samme
        mapping, rådata og historik som dine øvrige kilder. “Læseværktøj” er
        serverens egen erklæring. Brug en separat nøgle, som kun kan læse hos
        kilden. Ændres server eller værktøj, kræves en ny godkendelse.
      </p>
      <button
        type="button"
        class="button secondary"
        :disabled="busy || !url"
        @click="discoverMcp"
      >
        {{ busy ? "Henter…" : "Hent læseværktøjer" }}
      </button>
      <label v-if="mcpTools.length"
        >Godkendt læseværktøj<select v-model="mcpTool">
          <option v-for="tool in mcpTools" :key="tool.name" :value="tool.name">
            {{ tool.name }}
          </option>
        </select></label
      >
      <p v-if="mcpTool" class="muted small">
        {{ mcpTools.find((t) => t.name === mcpTool)?.description }}
      </p>
      <label
        >Argumenter (JSON)<textarea
          v-model="mcpArguments"
          class="code-input"
          rows="4"
          spellcheck="false"
        />
      </label>
      <details v-if="mcpTool">
        <summary>Værktøjets argumenter og sideopdeling</summary>
        <pre class="source-preview-json">{{
          JSON.stringify(
            mcpTools.find((t) => t.name === mcpTool)?.inputSchema,
            null,
            2,
          )
        }}</pre>
        <div class="form-grid">
          <label
            >Cursor-argument, valgfrit<input
              v-model="mcpCursorArgument"
              placeholder="Fx cursor" /></label
          ><label
            >Sti til næste cursor<input
              v-model="mcpNextCursorPath"
              :required="!!mcpCursorArgument"
              placeholder="Fx nextCursor"
          /></label>
        </div>
      </details>
    </template>
    <h4>Mapping</h4>
    <div class="form-grid">
      <label
        v-for="f in fields.filter((f) => f.active && !f.calculation)"
        :key="f.id"
        >Kildesti til {{ f.label }}<input v-model="paths[f.id]" required
      /></label>
    </div>
    <div class="form-actions">
      <button type="button" class="button secondary" @click="adding = false">
        Annullér</button
      ><button
        class="button primary"
        :disabled="busy || (kind === 'mcp' && !mcpTool)"
      >
        Opret kilde
      </button>
    </div>
  </form>
  <div v-if="!sources.length && !adding" class="empty-state">
    <LinkIcon :size="28" />
    <h3>Forbind din første kilde</h3>
    <p>HTTP, MCP, filprofiler og webhooks skriver til det samme datasæt.</p>
  </div>
  <div class="source-list">
    <article v-for="s in sources" :key="s.id" class="source-row">
      <div class="source-summary">
        <span class="dataset-icon"
          ><LinkIcon
            v-if="['http', 'mcp'].includes(s.kind)"
            :size="21" /><UploadIcon
            v-else-if="s.kind === 'file'"
            :size="21" /><WebhookIcon v-else :size="21"
        /></span>
        <div>
          <h3>{{ s.name }}</h3>
          <p>
            {{
              ["http", "mcp"].includes(s.kind)
                ? s.config.url + (s.kind === "mcp" ? " · " + s.config.tool : "")
                : s.kind === "file"
                  ? "Genanvendelig " + s.config.format.toUpperCase() + "-profil"
                  : "Signeret webhook"
            }}
            · Mapping v{{ s.active_mapping_version }}
          </p>
          <small v-if="schedules.find((x) => x.target_id === s.binding_id)"
            >Næste kørsel:
            {{
              dateTime(
                schedules.find((x) => x.target_id === s.binding_id).next_run_at,
              )
            }}
            ·
            {{
              schedules.find((x) => x.target_id === s.binding_id).timezone
            }}</small
          >
        </div>
        <span class="status-pill" :class="s.enabled ? 'success' : 'neutral'">{{
          s.enabled ? "Aktiv" : "Stoppet"
        }}</span>
      </div>
      <div class="source-actions">
        <button
          v-if="['http', 'mcp'].includes(s.kind)"
          class="button secondary"
          :disabled="busy || !s.enabled"
          @click="run(s)"
        >
          <PlayIcon :size="16" />Kør nu</button
        ><label v-if="s.kind === 'file'" class="button secondary file-button"
          ><UploadIcon :size="16" />Upload fil<input
            type="file"
            :accept="s.config.format === 'csv' ? '.csv' : '.xlsx'"
            :disabled="busy"
            @change="upload(s, $event)" /></label
        ><button
          v-if="['http', 'mcp'].includes(s.kind)"
          class="text-button"
          @click="scheduleSource = s.binding_id"
        >
          <ClockIcon :size="16" />Tidsplan</button
        ><button
          class="text-button"
          @click="
            mappingEdit = s;
            mappingText = JSON.stringify(s.mapping, null, 2);
          "
        >
          Redigér mapping</button
        ><button class="text-button" :disabled="busy" @click="proposeFields(s)">
          Foreslå nye felter</button
        ><button
          v-if="session.capabilities.ai"
          class="text-button"
          :disabled="busy"
          @click="proposeFields(s, true)"
        >
          Foreslå felter med AI</button
        ><button
          class="text-button"
          @click="
            action(async () => {
              await api(base + '/sources/' + s.id, {
                method: 'PATCH',
                body: { enabled: !s.enabled },
              });
            })
          "
        >
          {{ s.enabled ? "Stop kilde" : "Aktivér kilde" }}
        </button>
      </div>
    </article>
  </div>
  <form
    v-if="scheduleSource"
    class="inline-editor form-stack"
    @submit.prevent="schedule"
  >
    <h3>Planlæg import</h3>
    <div class="form-grid">
      <label
        >Cron (fem felter)<input
          v-model="cron"
          placeholder="0 9 * * *"
          required
        /><small>0 9 * * * kører hver dag kl. 09.</small></label
      ><label>Tidszone<input v-model="timezone" required /></label>
    </div>
    <p class="muted small">
      Ved nedetid køres højst én indhentning. Overlappende importer flettes til
      den aktive kørsel.
    </p>
    <div class="form-actions">
      <button
        type="button"
        class="button secondary"
        @click="scheduleSource = ''"
      >
        Annullér</button
      ><button class="button primary" :disabled="busy">Gem tidsplan</button>
    </div>
  </form>
  <section v-if="drift" class="inline-editor form-stack">
    <h3>Nye felter fra {{ drift.source.name }}</h3>
    <p class="muted">
      Fundet i dataprøven: {{ drift.newPaths.join(", ") }}. Gennemgå felterne,
      før du godkender schema og mapping.
    </p>
    <p v-for="note in drift.notes" :key="note" class="small">{{ note }}</p>
    <FieldEditor v-model="drift.schema.fields" existing />
    <div class="form-actions">
      <button class="button secondary" @click="drift = null">
        Annullér feltforslag</button
      ><button class="button primary" :disabled="busy" @click="previewFields">
        Se preview af nye felter
      </button>
    </div>
    <div v-if="driftPreview">
      <p>
        {{ driftPreview.total }} eksisterende rækker undersøgt ·
        {{ driftPreview.invalid }} ugyldige
      </p>
      <p v-for="(e, i) in driftPreview.errors" :key="i" class="field-error">
        {{ e.issues.map((x: any) => x.message).join(" ") }}
      </p>
      <button
        class="button primary"
        :disabled="busy || driftPreview.invalid > 0"
        @click="acceptFields"
      >
        Godkend felter og fortsæt til mapping
      </button>
    </div>
  </section>
  <form
    v-if="mappingEdit"
    class="inline-editor form-stack"
    @submit.prevent="previewMapping"
  >
    <h3>Mapping · {{ mappingEdit.name }}</h3>
    <p class="muted">
      Stabile felt-ID’er må ikke ændres. Brug ignoredPaths til felter, der skal
      ignoreres fremover.
    </p>
    <label
      >Mapping-definition<textarea
        v-model="mappingText"
        class="code-input"
        rows="16"
        spellcheck="false"
      />
    </label>
    <div class="form-actions">
      <button
        type="button"
        class="button secondary"
        @click="mappingEdit = null"
      >
        Annullér</button
      ><button class="button secondary" :disabled="busy">
        Se mapping-preview
      </button>
    </div>
    <div v-if="mappingPreview">
      <p>
        {{ mappingPreview.checked }} eksempelrækker undersøgt ·
        {{ mappingPreview.invalid }} ugyldige
      </p>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th v-for="f in fields.filter((f) => f.active)" :key="f.id">
                {{ f.label }}
              </th>
              <th>Validering</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in mappingPreview.preview" :key="i">
              <td v-for="f in fields.filter((f) => f.active)" :key="f.id">
                {{ formatValue(row.data[f.id], f) }}
              </td>
              <td>
                {{
                  row.issues.length
                    ? row.issues.map((e: any) => e.message).join(" ")
                    : "Gyldig"
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <button
        type="button"
        class="button primary"
        :disabled="busy || mappingPreview.invalid > 0"
        @click="saveMapping"
      >
        Aktivér mapping
      </button>
    </div>
  </form>
  <div v-if="uploadPreview" class="inline-editor">
    <h3>Filpreview · {{ uploadPreview.total }} rækker</h3>
    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th
              v-for="f in fields.filter((f) => f.active && !f.hidden)"
              :key="f.id"
            >
              {{ f.label }}
            </th>
            <th>Validering</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in uploadPreview.preview" :key="i">
            <td
              v-for="f in fields.filter((f) => f.active && !f.hidden)"
              :key="f.id"
            >
              {{ row.data[f.id] ?? "—" }}
            </td>
            <td>
              {{
                row.issues.length
                  ? row.issues.map((x: any) => x.message).join(" ")
                  : "Klar"
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="form-actions">
      <button class="button secondary" @click="uploadPreview = null">
        Annullér</button
      ><button
        class="button primary"
        :disabled="
          busy || uploadPreview.preview.some((r: any) => r.issues.length)
        "
        @click="commitUpload"
      >
        Godkend import
      </button>
    </div>
  </div>
  <details v-if="raws.length" class="raw-history">
    <summary>Rådata og genbehandling ({{ raws.length }})</summary>
    <p class="muted">
      Originale bytes bevares normalt i 14 dage. Preview ændrer ingen records.
    </p>
    <div v-for="r in raws" :key="r.id" class="raw-row">
      <div>
        <strong>{{ dateTime(r.fetched_at) }}</strong
        ><span
          >{{ (Number(r.size_bytes) / 1024).toFixed(1) }} KiB · udløber
          {{ dateTime(r.expires_at) }}</span
        >
      </div>
      <button class="text-button" :disabled="busy" @click="rawPreview(r)">
        <ReplayIcon :size="16" />Preview genbehandling
      </button>
    </div>
    <div v-if="reprocess" class="inline-editor">
      <h3>Resultat af preview</h3>
      <p>{{ reprocess.valid }} gyldige · {{ reprocess.invalid }} afviste</p>
      <p v-for="e in reprocess.errors" :key="e" class="field-error">{{ e }}</p>
      <div class="form-actions">
        <button class="button secondary" @click="reprocess = null">Luk</button
        ><button
          class="button primary"
          :disabled="busy || reprocess.invalid > 0"
          @click="applyReprocess"
        >
          Start genbehandling
        </button>
      </div>
    </div>
  </details>
</template>
<style scoped lang="scss" src="../styles/components/SourcePanel.scss"></style>
